<template>
  <router-link
    v-if="loaded && currentUserHasAccess"
    class="s-chatLinkCard"
    :to="internalPath">
    <div class="s-chatLinkCard__imageWrapper">
      <template v-if="roundedImages.length">
        <div class="s-chatLinkCard__imageGroup">
          <img
            v-for="(img, idx) in roundedImages"
            :key="idx"
            :src="img"
            alt=""
            class="s-chatLinkCard__imageGroupItem" />
        </div>
      </template>
      <img
        v-else-if="singleImage"
        :src="singleImage"
        alt=""
        class="s-chatLinkCard__image" />
      <img
        v-else-if="defaultImage"
        :src="defaultImage"
        alt=""
        class="s-chatLinkCard__image" />
    </div>
    <div class="s-chatLinkCard__content">
      <div class="s-chatLinkCard__title t-h6">{{ title }}</div>
      <div
        v-if="deniedUsers.length"
        class="s-chatLinkCard__locks">
        <span
          v-for="user in deniedUsers"
          :key="user.id"
          v-popper="{ content: $t('chat.no_access_tooltip', { username: '<b>' + user.username + '</b>' }), html: true }"
          class="c-badge -tiny -new has-tooltip s-chatLinkCard__accessDeniedBadge"
          tabindex="0">
          <SvgIcon
            type="access_lock"
            tiny
            class="c-badge__baselineIcon c-popper__activator u-marginRight1" />
          <span class="t-caption1">{{ user.username }}</span>
        </span>
      </div>
    </div>
  </router-link>
  <div
    v-else-if="loaded && !currentUserHasAccess"
    class="s-chatLinkCard -locked">
    <div class="s-chatLinkCard__imageWrapper">
      <div class="s-chatLinkCard__image -locked">
        <SvgIcon
          type="access_lock"
          larger />
      </div>
    </div>
    <div class="s-chatLinkCard__content">
      <div class="s-chatLinkCard__title t-caption1">{{ $t('generic.access_denied') }}</div>
    </div>
  </div>
  <div
    v-else
    class="s-chatLinkCard -loading">
    <div class="s-chatLinkCard__imageWrapper">
      <div class="s-chatLinkCard__image -loading">
        <LoadingSpinner />
      </div>
    </div>
    <div class="s-chatLinkCard__content">
      <div class="s-chatLinkCard__title t-caption1">{{ $t('generic.loading') }}</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

import LoadingSpinner from '@/components/LoadingSpinner.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import constants from '@/constants'
import { useContentAccessStore } from '@/modules/chat/contentAccessStore'
import store from '@/store'

const props = defineProps({
  href: { type: String, required: true },
  userIds: { type: Array, required: false, default: () => [] },
  morphType: { type: Number, required: true },
  foreignId: { type: Number, required: true },
})

const loaded = ref(false)
const currentUserHasAccess = ref(false)
const coverImageUrl = ref(null)
const title = ref('')
const deniedUsers = ref([])

const internalPath = computed(() => props.href.replace(/^https?:\/\/[^/]+/, '') || '/')

const contentAccessStore = useContentAccessStore()

const coverImages = computed(() => {
  const src = coverImageUrl.value
  if (Array.isArray(src)) {
    return src.filter(Boolean)
  }
  return src ? [src] : []
})

const isCategory = computed(() => props.morphType === constants.MORPH_TYPES.TYPE_CATEGORY)

const defaultImage = computed(() => {
  const config = store.getters['auth/config']
  // There is no default value for appointment
  // And for game we will handle the game image depending on players avatars
  const availableMorphTypes = {
    [constants.MORPH_TYPES.TYPE_LEARNINGMATERIAL]: 'media_library_default_image',
    [constants.MORPH_TYPES.TYPE_COURSE]: 'course_default_image',
    [constants.MORPH_TYPES.TYPE_NEWS]: 'news_default_image',
    [constants.MORPH_TYPES.TYPE_CATEGORY]: 'question_category_default_icon',
  }
  return config?.settings?.[availableMorphTypes[props.morphType]] ?? '/img/no-connection.svg'
})

// roundedImages: Game has no cover image, so we show players avatars,
// and category default icon, we have them in roundedImages so we can have the same circled style
const roundedImages = computed(() => {
  if (coverImages.value.length > 1) return coverImages.value
  // We return the default category icon and the content is a category (that means the category has no image, since coverImages.value length is 0)
  // and the category icon will be pixelated and not have a high resolution if we treat it as a big image not rounded
  if (isCategory.value && !coverImages.value.length && defaultImage.value) return [defaultImage.value]
  return []
})

// singleImage: All our content types except category that has no
// cover image(We show its default icon) or the game we show players avatars
const singleImage = computed(() => {
  if (coverImages.value.length === 1) return coverImages.value[0]
  if (!isCategory.value && !coverImages.value.length && defaultImage.value) return defaultImage.value
  return '/img/no-connection.svg'
})

const checkContentAccess = async () => {
  try {
    const data = await contentAccessStore.getContentAccessData(
      props.morphType,
      props.foreignId,
      props.userIds,
    )

    coverImageUrl.value = data.entity?.cover_image_url
    title.value = data.entity?.title
    currentUserHasAccess.value = data.currentUserHasAccess
    deniedUsers.value = data.deniedUsers
  } catch (e) {
    currentUserHasAccess.value = false
    console.log(e)
  } finally {
    loaded.value = true
  }
}
checkContentAccess()
</script>

<style scoped lang="scss">
@import '@/assets/scss/config';

.s-chatLinkCard {
  display: flex;
  text-decoration: none;
  border: 1px solid $color-dark_light_emphasis;
  border-radius: $spacing-4;
  background: $color-white;
  width: 300px;
  &.-locked {
    opacity: 0.9;
  }
  &.-loading {
    opacity: 0.8;
  }
}

.s-chatLinkCard__imageWrapper {
  width: 100%;
  flex: 0 0 auto;
  background: $color-comment-child-dots;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Clip only the image area so tooltips can overflow the card */
  overflow: hidden;
  border-top-left-radius: $spacing-4;
  border-top-right-radius: $spacing-4;
}

.s-chatLinkCard__image {
  aspect-ratio: 16/9;
  width: 100%;
  object-fit: scale-down;
  &.-locked {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &.-loading {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

// When multiple images are present, render them as circular avatars
.s-chatLinkCard__imageGroup {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-4;
  padding: $spacing-4 0;
}

.s-chatLinkCard__imageGroupItem {
  width: 75px;
  height: 75px;
  border-radius: 50%;
  object-fit: scale-down;
  border: 2px solid $color-white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.s-chatLinkCard__content {
  flex: 1;
  min-width: 0;
  border-top: 1px solid $color-dark_light_emphasis;
  padding: $spacing-2 $spacing-4;
}

.s-chatLinkCard__title {
  color: $color-dark;
}

.s-chatLinkCard__locks {
  margin-top: $spacing-2;
  display: flex;
  flex-wrap: wrap;
  gap: $spacing-1;
  .s-chatLinkCard__accessDeniedBadge {
    color: $color-dark_medium_emphasis !important;
    .c-badge__baselineIcon {
      margin-right: 0;
    }
  }
}
</style>
